import EmptyState from '@atlaskit/empty-state';
import { Stack } from '@atlaskit/primitives';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { graphql, type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { fg } from '../../../../controllers/feature-gates';
import { useGetCodePlannerSettingsController } from '../hooks/use-get-code-planner-settings-controller';
import type { JiraProject } from '../types';

import { type codePlannerConfigurationQuery } from './__generated__/codePlannerConfigurationQuery.graphql';
import { JiraProjectsTable } from './jira-projects-table';
import messages from './messages';
import { SiteSettings } from './site-settings';
import { enableCodePlannerSiteSettingsFeatureGateName } from './types';

type JiraProjectsConfigurationProps = {
	queryRef: PreloadedQuery<codePlannerConfigurationQuery>;
};

export const CodePlannerConfiguration = ({ queryRef }: JiraProjectsConfigurationProps) => {
	const [jiraProjectsLoaded, setJiraProjectsLoaded] = useState<boolean>(false);
	const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
	const { codePlannerSettingsCache, setCodePlannerSettingsCache } =
		useGetCodePlannerSettingsController();
	const { formatMessage } = useIntl();

	const isEnableAllRepositoriesButtonEnabled = fg(enableCodePlannerSiteSettingsFeatureGateName);

	const data = usePreloadedQuery<codePlannerConfigurationQuery>(
		graphql`
			query codePlannerConfigurationQuery($cloudId: ID!, $query: String!) {
				jira {
					jiraProjectsByJql(cloudId: $cloudId, query: $query) {
						totalCount
						pageInfo {
							startCursor
							endCursor
						}
						edges {
							node {
								id
								key
								name
								webUrl
							}
							cursor
						}
					}
				}
			}
		`,
		queryRef,
	);

	const projects = data?.jira?.jiraProjectsByJql?.edges;

	useEffect(() => {
		if (projects) {
			const projectNodes = projects
				.map((project) => project?.node as JiraProject)
				.filter((project): project is JiraProject => project !== null);
			setJiraProjects(projectNodes);
			setJiraProjectsLoaded(true);
		}
	}, [projects]);

	if (jiraProjectsLoaded && jiraProjects.length === 0) {
		return (
			<EmptyState
				testId="code-planner-no-projects-found"
				header={formatMessage(messages.emptyStateHeader)}
				description={formatMessage(messages.emptyStateDescription)}
			/>
		);
	}

	return (
		<Stack testId="code-planner-configuration">
			{isEnableAllRepositoriesButtonEnabled && (
				<SiteSettings projects={jiraProjects} onChange={setCodePlannerSettingsCache} />
			)}
			<JiraProjectsTable
				projects={jiraProjects}
				projectsLoaded={jiraProjectsLoaded}
				settingsCache={codePlannerSettingsCache}
				onSettingsCacheChange={setCodePlannerSettingsCache}
			/>
		</Stack>
	);
};
